import {EDjangoAppsEndpoints} from '../enums/EDjangoAppsEndpoints';
import {type PaginatedBackendResponse} from '../models/GhostPostModel';
import {type ISortingMeta} from '../models/ISortingMeta';
import {type ITemperatureScore} from '../models/ITemperatureScore';
import {type PlotlyResponse} from '../models/PlotlyModel';
import {type TPandasTable} from '../models/TPandasTable';
import {type IFactsheetFactorExposurePlots} from '../models/factsheet/IFactsheetFactorExposurePlots';
import {type TEsgPillars} from '../models/factsheet/TEsgPillars';
import {
  type FactsheetAdminDetail,
  type FactsheetAdminSummary,
  type FactsheetLinkCreationForm,
  type FactsheetResponse,
  type SelfServiceSignUpForm,
} from '../models/publicFactsheetModel';
import { type IAccessLinkInquiryDetail, } from '../models/IAccessLinkInquiryDetail';
import ApiService from './apiService';
import { type IFactsheetInquiryUpdateResponse } from '../models/IAccessLinkInquiryUpdateResponse';

export default class PublicFactsheetServices extends ApiService {
  /**
   * @deprecated Ticket #589.
   * @param factsheetId
   */
  static async getPublicFactsheet(factsheetId: string) {
    return await super.get<FactsheetResponse>(EDjangoAppsEndpoints.factsheet, `public/${factsheetId}`);
  }

  static async getFactsheetCounter(id: string, userType: 'public' | 'user') {
    return await super.get(EDjangoAppsEndpoints.factsheet, `${userType}/counter/${id}`);
  }

  static async getFactsheetChart(id: string) {
    return await super.get<PlotlyResponse>(EDjangoAppsEndpoints.factsheet, `public/overview/chart/${id}`);
  }

  static async getFactsheetOverviewTable(id: string) {
    return await super.get<TPandasTable>(EDjangoAppsEndpoints.factsheet, `public/overview/table/${id}`);
  }

  static async getFactorExposurePlots(factsheetId: string) {
    return await super.get<IFactsheetFactorExposurePlots>(
      EDjangoAppsEndpoints.factsheet,
      `public/factor-exposure/charts/${factsheetId}`,
    );
  }

  static async getFactorExposureCfer(
    id: string,
    type: 'universe' | 'swap',
    page: number,
    sorting: ISortingMeta,
    searchText?: string,
  ) {
    return await super.get<PaginatedBackendResponse<TPandasTable>>(
      EDjangoAppsEndpoints.factsheet,
      `public/factor-exposure/cfer/${id}`,
      {
        type,
        page,
        ...sorting,
        searchText,
      }
    );
  }

  static async getFactorExposureTable(id: string) {
    return await super.get<{ universe: TPandasTable; swap: TPandasTable }>(
      EDjangoAppsEndpoints.factsheet,
      `public/factor-exposure/table/${id}`,
    );
  }

  static async getSectorExposureUniverseSwapTables(id: string, sorting?: ISortingMeta) {
    return await super.get<Record<'universe' | 'swap', TPandasTable>>(
      EDjangoAppsEndpoints.factsheet,
      `public/sector-exposure/table/${id}`,
      {
        ...sorting
      }
    );
  }

  static async getSectorExposureCharts(id: string) {
    return await super.get<
      Record<
        'industryMcap' | 'exchangeIndustryMcap' | 'industryAllocationWeight' | 'exchangeIndustryAllocationWeight',
        Record<'universe' | 'swap', PlotlyResponse>
      >
    >(EDjangoAppsEndpoints.factsheet, `public/sector-exposure/charts/${id}`);
  }

  static async getSectorAssignmentTable(
    id: string,
    table: 'universe' | 'swap',
    page: number,
    sorting?: ISortingMeta,
    sector?: string,
    search?: string,
    filters?: string[]
  ) {
    return await super.get<PaginatedBackendResponse<TPandasTable>>(
      EDjangoAppsEndpoints.factsheet,
      `public/sector-exposure/assignment/${id}`,
      {
        table,
        page,
        sector,
        ...sorting,
        search,
        filters: filters?.join(',')
      }
    );
  }

  static async getEsgPerformance(id: string) {
    return await super.get<TEsgPillars>(EDjangoAppsEndpoints.factsheet, `public/esg/esg-performance/${id}`);
  }

  static async getEsgRisk(id: string) {
    return await super.get<TEsgPillars>(EDjangoAppsEndpoints.factsheet, `public/esg/esg-risk/${id}`);
  }

  static async getExclusionTable(
    id: string,
    page: number,
    sorting: ISortingMeta,
    search?: string,
    filters?: string[],
  ) {
    return await super.get<PaginatedBackendResponse<TPandasTable>>(
      EDjangoAppsEndpoints.factsheet,
      `public/esg/exclusion-table/${id}`,
      {
        page,
        search,
        ...sorting,
        filters: filters?.join(',')
      }
    );
  }

  static async getTemperatureScore(id: string) {
    return await super.get<ITemperatureScore>(EDjangoAppsEndpoints.factsheet, `public/esg/temperature-score/${id}`);
  }

  /**
   * @deprecated Ticket #589.
   */
  static async getDeltaDistribution(id: string) {
    return await super.get<PlotlyResponse>(EDjangoAppsEndpoints.factsheet, `public/delta/distribution/${id}`);
  }

  /**
   * @deprecated Ticket #589.
   */
  static async getDeltaChangeMatrix(id: string) {
    return await super.get<PlotlyResponse>(EDjangoAppsEndpoints.factsheet, `public/delta/change-matrix/${id}`);
  }

  /**
   * @deprecated Ticket #589.
   */
  static async getDeltaPlots(id: string) {
    return await super.get<{ industries: PlotlyResponse; factors: PlotlyResponse }>(
      EDjangoAppsEndpoints.factsheet,
      `public/delta/plots/${id}`,
    );
  }

  /**
   * @deprecated Ticket #589.
   */
  static async getDeltaLeverage(id: string) {
    return await super.get<PlotlyResponse>(EDjangoAppsEndpoints.factsheet, `public/delta/leverage/${id}`);
  }

  static async getPerformancePlot(id: string, startDate?: string, endDate?: string) {
    return await super.get<PlotlyResponse>(EDjangoAppsEndpoints.factsheet, `public/performance/plot/${id}`, {
      startDate,
      endDate,
    });
  }

  static async getPerformanceTable(id: string, startDate?: string, endDate?: string) {
    return await super.get<TPandasTable>(EDjangoAppsEndpoints.factsheet, `public/performance/table/${id}`, {
      startDate,
      endDate,
    });
  }

  // PERFORMANCE ATTRIBUTION

  /**
   * @deprecated Ticket #589.
   */
  static async getPerformanceAttributionOverview(id: string) {
    return await super.get<PlotlyResponse>(
      EDjangoAppsEndpoints.factsheet,
      `public/performance-attribution/overview/${id}`);
  }

  /**
   * @deprecated Ticket #589.
   */
  static async getPerformanceAttributionPerformanceComponents(id: string) {
    return await super.get<PlotlyResponse>(
      EDjangoAppsEndpoints.factsheet,
      `public/performance-attribution/performance-components/${id}`);
  }

  static async getPerformanceAttributionStockSelectionChart(id: string) {
    return await super.get<{ withoutLeverage: PlotlyResponse; withLeverage: PlotlyResponse }>(
      EDjangoAppsEndpoints.factsheet,
      `public/performance-attribution/stock-selection/chart/${id}`);
  }

  static async getPerformanceAttributionStockSelectionTable(
    id: string,
    params: { startDate: string; endDate: string }
  ) {
    return await super.get<{ table: TPandasTable; startDate: string; endDate: string }>(
      EDjangoAppsEndpoints.factsheet,
      `public/performance-attribution/stock-selection/table/${id}`,
      params
    );
  }

  static async getPerformanceAttributionBetaTransformations(id: string) {
    return await super.get<PlotlyResponse>(
      EDjangoAppsEndpoints.factsheet,
      `public/performance-attribution/beta-transformation/${id}`);
  }

  static async getPerformanceAttributionRiskProfileChart(id: string) {
    return await super.get<PlotlyResponse>(
      EDjangoAppsEndpoints.factsheet,
      `public/performance-attribution/risk-profile/chart/${id}`);
  }

  static async getPerformanceAttributionRiskProfileTable(
    id: string,
    params: { startDate: string; endDate: string }
  ) {
    return await super.get<{ table: TPandasTable; startDate: string; endDate: string }>(
      EDjangoAppsEndpoints.factsheet,
      `public/performance-attribution/risk-profile/table/${id}`,
      params
    );
  }

  static async getPerformanceAttributionBreakdowns(id: string) {
    return await super.get<{ economic: PlotlyResponse; instrumental: PlotlyResponse }>(
      EDjangoAppsEndpoints.factsheet,
      `public/performance-attribution/breakdowns/${id}`);
  }

  // Risk Utilization
  static async getDeltaCrossSectionalDistribution(id: string) {
    return await super.get<{ equity_exposure: PlotlyResponse; position_delta: PlotlyResponse }>(
      EDjangoAppsEndpoints.factsheet,
      `public/delta/cross-sectional-distribution/${id}`);
  }

  static async getDeltaChangeMatrixGrouped(id: string) {
    return await super.get<{
      risk_utilization: PlotlyResponse;
      average_delta: PlotlyResponse;
      delta_over_time: PlotlyResponse;
    }>(EDjangoAppsEndpoints.factsheet, `public/delta/delta-by-factor/${id}`);
  }

  static async getDeltaBySectorGrouped(id: string) {
    return await super.get<{
      risk_utilization: PlotlyResponse;
      average_delta: PlotlyResponse;
      delta_over_time: PlotlyResponse;
    }>(EDjangoAppsEndpoints.factsheet, `public/delta/delta-by-sector/${id}`);
  }

  // TODO: Move methods below to "fact sheet link services"

  /**
   * @deprecated Ticket #589.
   */
  static async getFactsheetAdminList() {
    return await super.get<{ factsheets: FactsheetAdminSummary[] }>(EDjangoAppsEndpoints.factsheet, 'admin/list');
  }

  static async getFactsheetAdminDetail(factsheetId: string) {
    return await super.get<FactsheetAdminDetail>(EDjangoAppsEndpoints.factsheet, `admin/id/${factsheetId}`);
  }

  static async getLastViewedFactsheet() {
    return await super.get<{ id: string | undefined }>(EDjangoAppsEndpoints.factsheet, 'last');
  }

  static async postFactsheetLink(form: Partial<FactsheetLinkCreationForm>) {
    return await super.post<{ id: string }>(EDjangoAppsEndpoints.factsheet, 'admin', form);
  }

  static async postSelfServiceSignUp(form: Partial<SelfServiceSignUpForm>) {
    return await super.post<{ success: boolean }>(EDjangoAppsEndpoints.factsheet, 'public/inquiry', form);
  }

  static async putFSLink(linkId: string, form: Partial<FactsheetAdminDetail>) {
    return await super.put<FactsheetAdminDetail>(EDjangoAppsEndpoints.factsheet, `admin/id/${linkId}`, form);
  }

  static async deleteFSLink(linkId: string) {
    return await super.delete<boolean>(EDjangoAppsEndpoints.factsheet, `admin/id/${linkId}`);
  }

  static async getInquiryDetails(linkId: string) {
    return await super.get<IAccessLinkInquiryDetail>(EDjangoAppsEndpoints.factsheet, `admin/inquiry/${linkId}`);
  }

  static async updateFactsheetLinkInquiryDetail(linkId: string, form: { status: string }) {
    return await super.post<IFactsheetInquiryUpdateResponse>(EDjangoAppsEndpoints.factsheet, `admin/inquiry/${linkId}`, form);
  }

  static async deleteFactsheetInquiry(linkId: string) {
    return await super.delete<{
      success: boolean
    }>(EDjangoAppsEndpoints.factsheet, `admin/inquiry/${linkId}`);
  }
}
